import { useState } from "react";
import { Icon, useSofortPopup, useTelefon } from "@kundeninfo/ui";
import type { ReactElement, ReactNode } from "react";
import { ApiError } from "@kundeninfo/api-client";
import { Abschnitt, Chip, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { berlinerTag, datumsMarke } from "./ticketFormat.js";
import { FREMD_STATUSART, FREMD_SYSTEM, fremdPerson, fremdSuche, leseFremdOffen, merkeFremdOffen, minutenText, standText, uhrzeitText, useFremdtickets }
  from "./fremdSicht.js";
import { FremdDialog } from "./FremdDialog.js";
import type { BoardFilter } from "./boardSpeicher.js";
import type { FremdticketsClient } from "../fremd/fremdClient.js";

/**
 * ABSCHNITT „FREMDTICKETS" DER LISTE (Spec Tickets, Nachtrag 1f, F143): über der Tabelle der eigenen Tickets, weil
 * der Server die eigene Liste ordnet und zu 100 Zeilen blättert — eingemischte Fremdtickets zerrissen die Seiten.
 * Ordnung „Schlechtestes oben" kommt vom Server; die Suche der Liste filtert mit; Archiv wirkt nicht. Dieselbe
 * Sichtregel wie die Fremdspalte (`fremdPerson`). Zuklappbar, gemerkt im Browser. „Jetzt abgleichen" nur für die
 * eigenen (F141, F147), gesperrt mit Sofort-Popup „Wieder möglich ab 14:32"; der Aufwand steht rechtsbündig.
 */
/** „gruppe/<wbr>fremd#5001“: Eine lange Kennung bricht nach dem Schrägstrich um statt mitten im Wort. */
function umbruchNachSchraegstrich(kennung: string): ReactNode[] {
  const teile = kennung.split("/");
  return teile.flatMap((teil, i) => (i === teile.length - 1 ? [teil] : [`${teil}/`, <wbr key={i} />]));
}

export function FremdAbschnitt(p: { client: FremdticketsClient; filter: BoardFilter;
                                    ich: { id: string; istAdmin: boolean; name?: string };
                                    suche: string; neuLaden?: number;
                                    /** Berliner Tag und Feiertage für die Schnellknöpfe des Dialogs — dieselben wie im
                                     *  Board (Review 9, N1); geladen beim ersten Öffnen. */
                                    kalender?: () => Promise<{ heute: string; feiertage: readonly string[] }> }):
    ReactElement | null {
  const person = fremdPerson(p.filter, p.ich);
  const { daten, neu } = useFremdtickets(p.client, person, p.ich.id, p.neuLaden);
  const [offen, setOffen] = useState(leseFremdOffen);
  const [dialog, setDialog] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  // Sichtprobe 1f, Befund 3: am Telefon kürzte der Titel mitten im Wort („Fre…“) — dort Kurzform und Symbolknopf.
  const telefon = useTelefon();
  const [kalender, setKalender] = useState<{ heute: string; feiertage: readonly string[] } | null>(null);
  const oeffnen = (id: string) => {
    setDialog(id);
    if (kalender === null && p.kalender !== undefined) {
      p.kalender().then(setKalender).catch(() => undefined);
    }
  };
  const gesperrtBis = daten?.naechsterManuellAb ?? null;
  const gesperrt = gesperrtBis !== null && Date.parse(gesperrtBis) > Date.now();
  const popup = useSofortPopup<HTMLSpanElement>(gesperrt ? `Wieder möglich ab ${uhrzeitText(gesperrtBis!)}`
    : daten?.letzterAbgleich == null ? "Jetzt abgleichen" : `Jetzt abgleichen · ${standText(daten.letzterAbgleich)}`);
  if (daten === null || daten.konten === 0) return null;
  const heute = berlinerTag(new Date().toISOString());
  const items = fremdSuche(daten.items, p.suche);
  const ueberfaellig = items.filter((k) => k.dueDate !== null && k.dueDate < heute).length;

  async function abgleichen(): Promise<void> {
    setMeldung(null);
    // Auch nach einem Fehler neu laden: Bei 429 bringt die Antwort die Sperre (`naechsterManuellAb`) mit.
    try { await p.client.syncFremdtickets(); }
    catch (f) { setMeldung(f instanceof ApiError ? f.message : "Der Abgleich ist gescheitert."); }
    neu();
  }

  return (
    <div className="tfa">
      {/* Anzahl in der Pille, die Überfälligen getrennt daneben (Review 9, N2: `fehler` ersetzte die Anzahl). */}
      <Abschnitt titel={telefon ? "Fremd" : "Fremdtickets"} zaehler={items.length} offen={offen}
        onUmgeschaltet={(d) => { setOffen(d.offen); merkeFremdOffen(d.offen); }}
        aktionen={(
          <>
            {ueberfaellig > 0 && <Chip className="tfa__ueberfaellig" statusArt="fehler">{ueberfaellig} überfällig</Chip>}
            {person === p.ich.id && (
              <span ref={popup}>
                {/* Sekundär statt leise (Sichtprobe 1f, Befund 1: leise las sich im Dunkelschema wie gesperrt). */}
                {telefon ? (
                  <KiKnopf name="Jetzt abgleichen" deaktiviert={gesperrt} onClick={() => { void abgleichen(); }}>
                    <Icon name="arrow-rotate-right" size={14} />
                  </KiKnopf>
                ) : <KiKnopf deaktiviert={gesperrt} onClick={() => { void abgleichen(); }}>Jetzt abgleichen</KiKnopf>}
              </span>
            )}
          </>
        )}>
        {meldung !== null && <Hinweis art="warnung">{meldung}</Hinweis>}
        {items.length === 0 ? <p className="tl__leer">Keine Fremdtickets.</p> : (
          <Tabelle stapeln>
            <table>
              <thead>
                {/* Feste Spaltenbreiten (Tickets.css, Sichtprobe 1f, Befund 2): Die Liste rechnet `table-layout: fixed`,
                    ohne Klassen wären alle sieben Spalten gleich breit und die Kennung liefe in den Titel. */}
                <tr><th scope="col" className="tfa__sp-quelle">Quelle</th><th scope="col" className="tfa__sp-kennung">Kennung</th>
                  <th scope="col" className="tfa__sp-titel">Titel</th><th scope="col" className="tfa__sp-status">Status</th>
                  <th scope="col" className="tfa__sp-faelligkeit">Fälligkeit</th>
                  <th scope="col" className="tfa__sp-aufwand" data-ausrichtung="rechts">Aufwand</th>
                  <th scope="col" className="tfa__sp-aktionen"><span className="fk__unsichtbar">Aktionen</span></th></tr>
              </thead>
              <tbody>
                {items.map((k) => {
                  const marke = datumsMarke(k.dueDate, heute);
                  return (
                    <tr key={k.id}>
                      <td><Chip>{FREMD_SYSTEM[k.system]}</Chip></td>
                      <td><a className="tfa__kennung" href={k.url} target="_blank" rel="noopener noreferrer">{umbruchNachSchraegstrich(k.externalKey)}</a></td>
                      <td className="tl__titel">{k.title}</td>
                      <td>{FREMD_STATUSART[k.remoteStateKind]}</td>
                      <td>{marke.art === null ? <Chip className={marke.ohne ? "tl__datum tl__datum--ohne" : "tl__datum"}>
                        {marke.ohne ? "ohne Fälligkeit" : marke.text}</Chip> : <Chip className="tl__datum" statusArt={marke.art}>{marke.text}</Chip>}</td>
                      <td data-ausrichtung="rechts">{k.timeSpentMinutes === null || k.timeSpentMinutes === 0 ? "–" : minutenText(k.timeSpentMinutes)}</td>
                      <td><KiKnopf name={`${k.externalKey} Fälligkeit …`} onClick={() => oeffnen(k.id)}>Fälligkeit …</KiKnopf></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Tabelle>
        )}
      </Abschnitt>
      {dialog !== null && (
        <FremdDialog client={p.client} id={dialog}
          {...(kalender !== null ? { heute: kalender.heute, feiertage: kalender.feiertage } : {})}
          {...(person === p.ich.id && p.ich.name !== undefined ? { zustaendig: p.ich.name } : {})}
          onSchliessen={(g) => { setDialog(null); if (g) neu(); }} />
      )}
    </div>
  );
}
